import { Injectable, Inject, ViewEncapsulation, RendererFactory2, PLATFORM_ID, EventEmitter, Optional, Renderer2 } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, Params, UrlSerializer } from '@angular/router';
import { Title, Meta, DomSanitizer } from '@angular/platform-browser';
import { filter, map, throttleTime, pairwise, distinctUntilChanged, share, takeUntil, startWith } from 'rxjs/operators';
import { DOCUMENT, isPlatformBrowser, PlatformLocation } from '@angular/common';
import { HttpClient, HttpContext, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, fromEvent, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { LogLevel, EventId, Utility, setTimeout$, EventBusType, safeStringify } from '../core/common';
import { environment } from 'src/environments/environment';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { SpinnerService } from './spinner.service';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { Store } from '@ngxs/store';
import * as Favico from 'favico.js';
import { AuthState } from '../states/auth.state';
import { Constants } from '../states/constants';

declare let $: any;

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private favico = new Favico({ fontFamily: 'iranyekan', animation: 'none' });
  badge(value?: any, options?: any) {
    if (value) this.favico.badge(value, options);
    else if (!environment.production)
      this.favico.badge(environment.app.env[0].toUpperCase(), { bgColor: '#EE8200' });
    else this.favico.reset();
  }

  constructor(private router: Router, private route: ActivatedRoute,
    private http: HttpClient, private titleService: Title, private meta: Meta,
    private platformLocationService: PlatformLocation, private _sanitizer: DomSanitizer,
    private rendererFactory: RendererFactory2, spinner: SpinnerService,
    private store: Store,
    @Optional() @Inject(RESPONSE) private response: Response,
    @Inject(PLATFORM_ID) platformId: any,
    @Inject(DOCUMENT) private document) {
    this.renderer2 = rendererFactory.createRenderer(null, null);

    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      try { this.isPwa = window.matchMedia('(display-mode: standalone)')?.matches; } catch { console.log() }
      document.addEventListener('mousedown', (event) => {
        this.eventBus$.next({ type: 'mousedown', payload: event });
      });
      AuthState.user$.subscribe((user) => { this.me$.next(Utility.clone(user)); });
    }

    this.activatedRoute$ = router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => route),
      map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter(route => route.outlet === 'primary')
    );

    this.activatedRoute$.subscribe(route => {
      this.currentUrl = route.snapshot.url.join('/');
      let queryString = Object.keys(route.snapshot.queryParams)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(route.snapshot.queryParams[key])).join('&');
      if (queryString) this.currentUrl += '?' + queryString;

      this.setTitleFromRoute();
      this.setHeaderFooter(route);
      this.navigationEnd$.next(route);
      spinner.hideAll();
      // this.addBodyClass();
    });

    var fs = (<any>window).RequestFileSystem || (<any>window).webkitRequestFileSystem;
    if (fs) fs((<any>window).TEMPORARY,
      100,
      () => this.isIncognito = false,
      () => this.isIncognito = true);

    this.online$ = merge(fromEvent(window, 'online'), fromEvent(window, 'offline')).pipe(map(() => navigator.onLine), startWith(navigator.onLine));
  }

  online$: Observable<boolean>;

  renderer2: Renderer2;
  waitForOtp(timeoutMin: number, callback: (otp: any) => void): boolean {
    try {
      if ('OTPCredential' in window) {
        const abort = new AbortController();
        setTimeout$(() => abort.abort(), timeoutMin * 60 * 1000);
        let options = { signal: abort.signal, otp: { transport: ['sms'] } };
        navigator.credentials.get(options)
          .then((otp) => callback(otp))
          .catch((error) => console.log('SMS receiving error: ' + error));
        return true;
      }
    } catch (error) { Utility.log.push('otp exception ' + JSON.stringify(error)); }
    return false;
  }
  static NO_LOADING_BAR = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
  currentUrl: string;
  get isAlreadySubscribed() {
    return !!(localStorage.getItem('kp-subscribe-search-subscribed'));
  }
  set isAlreadySubscribed(value: boolean) {
    if (value)
      localStorage.setItem('kp-subscribe-search-subscribed', 'true');
    else
      localStorage.removeItem('kp-subscribe-search-subscribed');
  }

  navigationEnd$ = new ReplaySubject<ActivatedRoute>();
  activatedRoute$ = new Observable<ActivatedRoute>();
  get activatedRouteParams(): Params {
    return { ...this.route.snapshot.params, ...this.route.snapshot.queryParams };
  }

  me$ = new BehaviorSubject<any>(null);
  get me(): any {
    return Utility.clone(this.store.selectSnapshot(state => state.auth.user));
  }

  getRouteData(key: string): any {
    let route = this.route;
    let res = route.snapshot.data[key];
    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot?.data[key])
        res = route.snapshot.data[key];
    }
    return res;
  }

  private setTitleFromRoute() {
    let title = this.getRouteData('title');
    this.setTitle(title);
  }

  addBodyClass(...classes: string[]) {
    var body = document.getElementsByTagName('body')[0];
    if (classes.length)
      body.classList.add(...classes);
    else body.className = "";
  }

  private setHeaderFooter(route: ActivatedRoute) {
    Utility.showHeader = !location.href.includes('header-off');
    Utility.showFooter = !location.href.includes('footer-off');
    if (this.getRouteData('hideFooter'))
      Utility.showFooter = false;
    if (this.getRouteData('hideHeader'))
      Utility.showHeader = false;
  }

  async getFileFromUrl(url: string): Promise<ArrayBuffer> {
    //add cache buster to url
    url = url + (url.includes('?') ? '&' : '?') + 'cb=' + Date.now();
    const file = await firstValueFrom(this.http.get(url, { responseType: 'arraybuffer' }));
    return file;
  }

  downloadBlob(data: ArrayBuffer, type: string, name: string) {
    let blob = new Blob([data], { type: type });
    let url = window.URL.createObjectURL(blob);

    let a = $('body').append(`<a id='_download' href='${url}' download='${name}' ></a>`);
    $('#_download').get(0).click();
    $('#_download').remove();

    if (Utility.mobile) {
      alert('فایل دریافت و در بخش دانلودها ذخیره شد.');
    }
  }

  embedUrl(url: string, width: string = '100px', height: string = '100px') {
    return this._sanitizer.bypassSecurityTrustHtml(
      `<embed src="${url}" width="${width}" height="${height}" />`
    );
  }

  getSetting(key?: string) {
    return this.http.get<any>(environment.app.apiUrl + 'shared/setting/' + (key || 'all')).toPromise();
  }
  saveSetting(key: string, value: any) {
    return this.http.post(environment.app.apiUrl + 'shared/setting/' + key, value).toPromise();
  }

  getConfig(key?: string) {
    return this.http.get<any>(environment.app.apiUrl + 'shared/config/' + (key || 'all')).toPromise();
  }
  setConfigJson(config: any) {
    return this.http.post(environment.app.apiUrl + 'shared/config', config).toPromise();
  }

  private siteTitle = 'کارپیشه';
  isBrowser: boolean;
  isPwa: boolean;
  isIncognito: boolean;

  private _skills: any[];

  private _educations: string[] = ['دیپلم', 'کاردانی', 'کارشناسی', 'کارشناسی‌ارشد', 'دکترا'];

  // private keywords = ['فرصت شغلی', 'آگهی استخدام', 'کاریابی', 'خبر', 'روزمه'];
  private keywords = ['استخدام', 'شغل', 'ای استخدام', 'شغل انلاين', 'کار', 'مشاغل', 'آگهی استخدام', 'استخدامی'];

  getQueryParamsObject(url: string): any {
    const parts = url.split('?');
    if (parts.length < 2) { return null; }
    const res = {};
    parts[1].split('&').forEach(_ => { const p = _.split('='); res[p[0]] = p.length > 1 ? p[1] : ''; });
    return res;
  }

  getDomain(url: string) {
    let result;
    let match;
    if (match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n\?\=]+)/im)) {
      result = match[1];
      if (match = result.match(/^[^\.]+\.(.+\..+)$/)) {
        result = match[1];
      }
    }
    return result;
  }
  async getSkills() {
    if (!this._skills) {
      this._skills = await this.http.get<string[]>(environment.app.apiUrl + 'metadata/skills').toPromise();
    }
    return this._skills;
  }
  getDegrees() { return this._educations; }

  redirectPermanent(url: string) {
    if (this.isBrowser) window.location.href = url;
    else this.platformLocationService.pushState(301, '', url);
  }
  setHttpStatusCode(status: number) {
    if (!this.isBrowser) this.response.status(status);
  }

  async uploadFile(file: File, key: string, data?: any, onprogress?: (progress: number) => void) {
    const formData = new FormData();
    if (file) { formData.append('file', file, file.name); }
    formData.append('data', data);
    return new Promise<any>(resolve => {
      this.http.post<any>(environment.app.apiUrl + 'shared/upload/' + key, formData, { reportProgress: true, observe: 'events' })
        .subscribe(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (onprogress) { onprogress(Math.round(100 * event.loaded / event.total)); }
              break;
            case HttpEventType.Response:
              resolve(event.body);
              break;
          }
        });
    });
  }
  setTitle(title?: string) {
    if (!title) { title = 'آگهی استخدام، کاریابی، جستجوی رزومه'; }
    if (!title.includes(this.siteTitle))
      title += ' | ' + this.siteTitle;

    this.titleService.setTitle(title);
    this.updateMeta('og:title', title);
    this.updateMeta('twitter:title', title);
    this.setDescriptionMeta();
  }
  setKeywordsMeta(...keywords: string[]) {
    this.updateMeta('keywords', [this.siteTitle, ...keywords, ...this.keywords].map(_ => _?.trim ? _.trim() : '').filter(_ => !!_ && _.length > 2).join(','));
  }

  setNoIndex(follow = false) { this.updateMeta('ROBOTS', 'noindex' + (follow ? ',follow' : '')); }
  resetNoIndex() { this.updateMeta('ROBOTS', 'index,follow'); }

  setDescriptionMeta(description?: string) {
    if (!description) { description = 'آگهی استخدام خبر فرصت شغلی کاریابی رزومه | کارجو کارفرما شرکت دانشگاه بانک بیمه | رشته کارشناس مدیریت مهندس پزشکی صنایع | تهران اصفهان خراسان ایران'; }
    // description += ' | ' + this.siteTitle;

    this.updateMeta('description', description, '', 'og', 'twitter');
  }

  updateMeta(name: string, content?: string, ...prefix: string[]) {
    if (prefix?.length) {
      for (var p of prefix)
        this.updateMeta(`${(p ? p + ':' : '')}${name}`, content);
      return;
    }

    const selector = 'name=\'' + name + '\'';
    const selectorP = 'property=\'' + name + '\'';
    if (!content) {
      this.meta.removeTag(selector);
      this.meta.removeTag(selectorP);
    } else {
      const elem = this.meta.getTag(selector);
      const elemP = this.meta.getTag(selectorP);
      if (elem) { this.meta.updateTag({ name, content }, selector); } else { this.meta.addTag({ name, content }); }
      if (elemP) { this.meta.updateTag({ property: name, content }, selectorP); } else { this.meta.addTag({ property: name, content }); }
    }
  }

  addScript(script: { code?: string, src?: string, async?: boolean, defer?: boolean, crossOrigin?: string }) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    if (script.src) s.src = script.src;
    s.text = script.code || '';
    if (script.async) s.async = script.async;
    if (script.defer) s.defer = script.defer;
    if (script.crossOrigin) s.crossOrigin = script.crossOrigin;
    this.renderer2.appendChild(this.document.body, s);
  }

  removeGeneralMeta(data: { type: string, name: string }) {
    try {
      const renderer = this.rendererFactory.createRenderer(this.document, { id: '-1', encapsulation: ViewEncapsulation.None, styles: [], data: {} });
      const head = this.document.head;
      if (head === null) {
        throw new Error('<head> not found within DOCUMENT.');
      }
      const elems = this.document.querySelectorAll(`meta[${data.type}=${data.name}]`);
      for (const el of elems) {
        renderer.removeChild(head, el);
      }
    } catch (e) {
      console.log('Error while removing tag ' + e.message);
    }

  }
  addGeneralMeta(data: { type: string, name: string, content: string }) {
    try {
      const renderer = this.rendererFactory.createRenderer(this.document, { id: '-1', encapsulation: ViewEncapsulation.None, styles: [], data: {} });

      const elem = renderer.createElement('meta');

      const head = this.document.head;

      if (head === null) {
        throw new Error('<head> not found within DOCUMENT.');
      }

      renderer.setAttribute(elem, data.type, data.name);
      renderer.setAttribute(elem, 'content', data.content);

      // [TODO]: get them to update the existing one (if it exists) ?
      renderer.appendChild(head, elem);

    } catch (e) {
      console.error('Error within linkService : ', e);
    }
  }

  addLink(tag: LinkDefinition) {

    try {
      const renderer = this.rendererFactory.createRenderer(this.document, {
        id: '-1',
        encapsulation: ViewEncapsulation.None,
        styles: [],
        data: {}
      });

      const link = renderer.createElement('link');

      const head = this.document.head;

      if (head === null) {
        throw new Error('<head> not found within DOCUMENT.');
      }

      Object.keys(tag).forEach((prop: string) => {
        return renderer.setAttribute(link, prop, tag[prop]);
      });

      // [TODO]: get them to update the existing one (if it exists) ?
      renderer.appendChild(head, link);

    } catch (e) {
      console.error('Error within linkService : ', e);
    }
  }

  removeLink(attrSelector: string) {
    if (attrSelector) {
      try {
        const renderer = this.rendererFactory.createRenderer(this.document, {
          id: '-1',
          encapsulation: ViewEncapsulation.None,
          styles: [],
          data: {}
        });
        const head = this.document.head;
        if (head === null) {
          throw new Error('<head> not found within DOCUMENT.');
        }
        const linkTags = this.document.querySelectorAll('link[' + attrSelector + ']');
        for (const link of linkTags) {
          renderer.removeChild(head, link);
        }
      } catch (e) {
        console.log('Error while removing tag ' + e.message);
      }
    }
  }

  scrollIntoView(selector: string, block: 'start' | 'center' | 'end' | 'nearest' = 'center', retry = 10, options?: ScrollIntoViewOptions) {
    if (!this.isBrowser) return;
    var element = document.querySelector(selector);
    if (!element) {
      if (retry > 0)
        setTimeout$(() => { this.scrollIntoView(selector, block, retry - 1, options); }, 100);
    }
    else setTimeout(() => {
      try { element.scrollIntoView(options || { behavior: 'smooth', block, inline: 'center' }); }
      catch { element.scrollIntoView(); }
    });
  }

  addClass(selector: string, className: string, removeAfterms?: number) {
    if (!this.isBrowser)
      return;
    $(selector).addClass(className);
    if (removeAfterms)
      setTimeout$(() => { $(selector).removeClass(className); }, removeAfterms);
  }

  scrollToTop(selector: string = undefined, top = 0) { this.scrollTo({ top: top }, selector); }
  scrollToBottom() { this.scrollTo({ top: document.body.scrollHeight }); }
  scrollTo(options: ScrollToOptions, selector: string = undefined) {
    if (!this.isBrowser)
      return;

    try {
      options = Utility.assign({ behavior: 'smooth' }, options);
      if (!this.isBrowser || !window || !window.scrollTo || !this.document) return;
      if (selector && !$(selector).get(0)) return;

      let target = $(selector || `.${Utility.layoutScrollClassname}`).get(0) || window;
      const scrollBehaviourEnabled = 'scrollBehavior' in document.documentElement.style;
      if (scrollBehaviourEnabled) {
        target.scrollTo(options);
      } else {
        target.scrollTo(options.left || 0, options.top || 0);
      }
    } catch (e) { console.error('sharedService.scrollTo', e); }
  }

  //scroll handlers. from https://netbasal.com/reactive-sticky-header-in-angular-12dbffb3f1d3
  scroll$(step = 1, selector: string = null) {
    if (!this.isBrowser) return of({ direction: undefined, scrollY: undefined });

    let container = selector ? $(selector) : window;
    return fromEvent(container, 'scroll').pipe(
      throttleTime(10),
      map(() => container.pageYOffset),
      pairwise(),
      map(([y1, y2]): { direction: ScrollDirection; scrollY: number; } => {
        return { direction: (y2 < y1 ? ScrollDirection.Up : ScrollDirection.Down), scrollY: y2 };
      }),
      map(_ => { return { direction: _.direction, scrollY: Math.floor(_.scrollY / step) * step } }),
      distinctUntilChanged((prev, curr) => prev.scrollY === curr.scrollY),
      share()
    );
  }

  scrollUp$(step = 1, selector: string = null) {
    return this.scroll$(step, selector).pipe(filter(_ => _.direction === ScrollDirection.Up));
  }
  scrollDown$(step = 1, selector: string = null) {
    return this.scroll$(step, selector).pipe(filter(_ => _.direction === ScrollDirection.Down));
  }

  //TODO: replace with eventBus$
  eventBus: EventEmitter<string> = new EventEmitter<string>();
  onEventBus(event: string, callback: () => void, unsubscribe$: Subject<any>) {
    this.eventBus.pipe(takeUntil(unsubscribe$)).subscribe(e => { if (e == event) callback(); });
  }

  eventBus$ = new Subject<EventBusType>();
  onEvent$<T>(...types: string[]) {
    return this.eventBus$.asObservable().pipe(
      filter(message => types.includes(message.type)),
      map(message => message.payload as T)
    );
  }


  stripHtmlTags(input: string, allowedTags: string[] = []) {
    try {
      allowedTags?.forEach(tag => input = input
        .replace(new RegExp(`<${tag}>`, 'g'), `&lt;${tag}&gt;`)
        .replace(new RegExp(`<\/${tag}>`, 'g'), `&lt;/${tag}&gt;`)
      );
      var fo = input.indexOf('<');
      var fc = input.indexOf('>');
      if (fc > -1 && (fo == -1 || fc < fo))
        input = input.substr(fc + 1);

      var lo = input.lastIndexOf('<');
      var lc = input.lastIndexOf('>');
      if (lo > -1 && (lc == -1 || lo > lc))
        input = input.substr(0, lo);

      input = input.replace(/<[^<>]+>/g, ' ').trim();

      return input.replace(/&lt;/g, `<`).replace(/&gt;/g, `>`);
      // let el = document.createElement('div');
      // el.innerHTML = input;
      // let res = el.innerText;
      // console.log('stripHtmlTags', input, el.outerHTML, el.innerText, res);
      // return res;

    } catch (error) {
      console.error('stripHtmlTags', error);
      return input;
    }
  }

  log(level: LogLevel, message: string, eventId?: EventId, data?: { [key: string]: any }) {
    data = data || {};
    data._appversion = environment.app.version;
    setTimeout$(() => {
      try {
        var log = { level, message, eventId, data: JSON.parse(safeStringify(data)) };
        if (!environment.production) console.log('sharedService.Log', log);
        else this.http.post(`${environment.app.apiUrl}shared/log`, log).subscribe(_ => { });
      }
      catch (e) { console.error(e); }
    });
  }


  // user
  getSuggestions(searchSource: string, term: string, count: number, types: string[]): Observable<{ Item1: string, Item2: string }[]> {
    return this.http.get<{ Item1: string, Item2: string }[]>(
      environment.app.apiUrl + `${searchSource}/suggest/${encodeURIComponent(term)}/${count}/${encodeURIComponent(types.join(','))}`,
      { context: SharedService.NO_LOADING_BAR }
    );
  }

  getReadable(value: any, translations: Record<string, string> | string): string | string[] {
    if (value == undefined) return '';
    if (typeof translations == 'string') translations = Constants.Translations[translations];
    if (translations == undefined) return value;
    return (Array.isArray(value))
      ? value.map(v => (v != undefined) ? (translations?.[v.toString()] || v) : '')
      : translations?.[value.toString()] || value;
  }


}

export enum ScrollDirection {
  Up = 'Up',
  Down = 'Down'
}


export declare type LinkDefinition = {
  charset?: string;
  crossorigin?: string;
  href?: string;
  hreflang?: string;
  media?: string;
  rel?: string;
  rev?: string;
  sizes?: string;
  target?: string;
  type?: string;
} & {
  [prop: string]: string;
};
